/**
 * @file Email Login & Signup component (dual mode).
 */

import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';

import Alert from '../global/Alert';
import EmailRegisterForm from './EmailRegisterForm';
import EmailLoginForm from './EmailLoginForm';
import { getErrorString } from '../../util/normalize-errors';
import { AUTH_TYPE } from '../../constants/auth';
import { ACCOUNT_REFERRAL_TYPE } from '../../constants/account-referrals';

export const STEP = {
  REGISTER: 'REGISTER',
  LOGIN: 'LOGIN',
};

class EmailLoginRegister extends Component {
  static propTypes = {
    accountLogin: PropTypes.func.isRequired,
    accountCreateByEmail: PropTypes.func.isRequired,
    loginSuccess: PropTypes.func.isRequired,
    onLogin: PropTypes.func,
    onRegister: PropTypes.func,
    // Fires both on login and on register
    onAuthSuccess: PropTypes.func,
    emailVerificationRedirectUrl: PropTypes.string,
    bypassEmailVerification: PropTypes.bool,
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.string,
    ]),
    defaultStep: PropTypes.oneOf(Object.values(STEP)),
    accountReferralCreate: PropTypes.func,
  };

  static defaultProps = {
    defaultStep: STEP.REGISTER,
    onLogin: () => {},
    onRegister: () => {},
    onAuthSuccess: () => {},
  };

  state = {
    loading: false,
    error: '',
    accountCreateSuccess: false,
    step: this.props.defaultStep,
  };

  onSwitchStep = event => {
    if (event) {
      event.preventDefault();
    }

    this.setState(({ step }) => ({
      step: step === STEP.REGISTER ? STEP.LOGIN : STEP.REGISTER,
      loading: false,
      error: '',
    }));
  };

  onRegister = async (email, password) => {
    const {
      accountCreateByEmail,
      emailVerificationRedirectUrl,
      loginSuccess,
      onRegister,
      onAuthSuccess,
      accountReferralCreate,
    } = this.props;

    this.setState(() => ({ loading: true, error: '' }));

    try {
      await accountCreateByEmail(email, password, emailVerificationRedirectUrl);
      await accountReferralCreate(ACCOUNT_REFERRAL_TYPE.ACCOUNT_SIGNUP);

      loginSuccess();

      this.setState(
        () => ({
          loading: false,
          accountCreateSuccess: true,
        }),
        () => {
          onRegister();
          onAuthSuccess();
        },
      );
    } catch (error) {
      this.setState(() => ({
        loading: false,
        error: getErrorString(error),
      }));
    }
  };

  onLogin = async (email, password) => {
    const { accountLogin, loginSuccess, onLogin, onAuthSuccess } = this.props;

    this.setState(() => ({ loading: true, error: '' }));

    try {
      await accountLogin(email, password);
      loginSuccess();

      this.setState(
        () => ({
          loading: false,
        }),
        () => {
          onLogin(AUTH_TYPE.EMAIL);
          onAuthSuccess(AUTH_TYPE.EMAIL);
        },
      );
    } catch (error) {
      this.setState(() => ({
        loading: false,
        error: 'Wrong email or password.',
      }));
    }
  };

  render() {
    const { loading, error, step, accountCreateSuccess } = this.state;
    const { children, bypassEmailVerification } = this.props;

    if (accountCreateSuccess && !bypassEmailVerification) {
      return (
        <Alert type="success">
          We just sent you an email to verify your email address. Please check
          your inbox and click the link to confirm your email address.
        </Alert>
      );
    }

    return (
      <Fragment>
        {step === STEP.REGISTER ? (
          <Fragment>
            <Alert type="info">
              Already have an account?{' '}
              <a href="#" className="alert-link" onClick={this.onSwitchStep}>
                Login here
              </a>
            </Alert>

            <h4>Create an account</h4>
            <EmailRegisterForm
              error={error}
              loading={loading}
              register={this.onRegister}
            />
          </Fragment>
        ) : (
          <Fragment>
            <Alert type="info">
              No account?{' '}
              <a href="#" className="alert-link" onClick={this.onSwitchStep}>
                Register here
              </a>
            </Alert>

            <h4>Sign in</h4>
            <EmailLoginForm
              login={this.onLogin}
              error={error}
              loading={loading}
            />
          </Fragment>
        )}

        {children}
      </Fragment>
    );
  }
}

export default EmailLoginRegister;
